import { AnalyticsBrowser } from '@segment/analytics-next';
import { deviceType } from '@/user-context';
import config from './config';
import { devLog } from './helpers/dev';
import { useCompanyStore } from './stores/company';
import { useNativeAppStore } from './stores/native-app';
import { usePageLayoutStore } from './stores/page-layout';
import { useUserStore } from './stores/user';

type SegmentLike = {
  page: (...args: any[]) => void;
  track: (...args: any[]) => void;
  identify: (...args: any[]) => void;
  group: (...args: any[]) => void;
  alias: (...args: any[]) => void;
  screen: (...args: any[]) => void;
  reset: (...args: any[]) => void;
  ready: (...args: any[]) => void;
  on: (...args: any[]) => void;
  off: (...args: any[]) => void;
  once: (...args: any[]) => void;
};

// Only needed for middleware typings (kept loose on purpose)
type SegmentMiddleware = (args: {
  payload: { obj?: any };
  next: (payload: any) => void;
}) => void;

const standardTrackProperties = () => {
  let platform = 'web';
  const { isNativeApp, isNativeAppIOS } = useNativeAppStore();
  const { screenSize } = usePageLayoutStore();
  const { user } = useUserStore();
  if (isNativeAppIOS) {
    platform = 'ios';
  } else if (isNativeApp) {
    platform = 'android';
  } else if (screenSize === 's') {
    platform = 'mobile';
  }

  const { company, companyId } = useCompanyStore();
  return {
    company_id: companyId,
    company_country_code: company?.country?.toUpperCase(),
    language: company?.language,
    impersonating: !!user?.impersonated,
    platform,
    device_type: deviceType,
  };
};

const createConsoleSegment = (): SegmentLike => {
  const log =
    (method: string) =>
    (
      event: string,
      properties?: Record<string, unknown>,
      ...rest: unknown[]
    ) => {
      const args =
        method === 'track' || method === 'page'
          ? [event, { ...standardTrackProperties(), ...properties }, ...rest]
          : [event, properties, ...rest];
      devLog(`Segment: ${method}`, { args });
    };

  return {
    page: log('page'),
    track: log('track'),
    identify: log('identify'),
    group: log('group'),
    alias: log('alias'),
    screen: log('screen'),
    reset: log('reset'),
    ready: log('ready'),
    on: log('on'),
    off: log('off'),
    once: log('once'),
  };
};

const addTrackStandardPropertiesMiddleware: SegmentMiddleware = ({
  payload,
  next,
}) => {
  const obj = payload?.obj;
  if (obj?.type === 'track' || obj?.type === 'page') {
    obj.properties = { ...standardTrackProperties(), ...obj.properties };
  }
  next(payload);
};

const stripPageDefaultsMiddleware: SegmentMiddleware = ({ payload, next }) => {
  const obj = payload?.obj;
  // In analytics-next, obj.type should be "page" for page events
  if (obj?.type === 'page') {
    const props = (obj.properties ??= {});
    delete props.url;
    delete props.path;
    delete props.referrer;
    delete props.search;
    delete props.title;
    delete props.name;
  }

  next(payload);
};

const disableSegment = config.isDevelopment || config.isTest;

export const segment: SegmentLike = disableSegment
  ? createConsoleSegment()
  : AnalyticsBrowser.load({ writeKey: config.segmentWriteKey || '' });

if (!config.isProduction) (window as any).segment = segment;

// Register middleware once the real analytics client is ready
if (!disableSegment) {
  // analytics-next returns a promise-like analytics instance; ready() is safe here
  (segment as any).ready(() => {
    (segment as any).addSourceMiddleware(addTrackStandardPropertiesMiddleware);
    (segment as any).addSourceMiddleware(stripPageDefaultsMiddleware);
  });
}
